// ============================================================================
// ui/shell/canvas-actions.js — Save · Share square buttons at the canvas TOP-LEFT
// ============================================================================
// Ports the Gym Layout Designer's `.settings-panel` cluster (Ryan 2026-07-25): square
// 40×40 toggles sitting on the canvas, each sliding a card DOWN from underneath it.
// Replaces the wide "Save & share" button that used to be frozen in the side pane's
// footer — the pane is for designing, the canvas corners are for acting on the design.
//
// The mechanism is the one WD already uses top-right for zoom (`createToolCluster` +
// a mounted popover), so this is a second instance of a proven pattern, not a new one.
// Two differences from GLD, both deliberate:
//   • GLASS. GLD's toggles are a flat white panel; ours wear `.wd-flat-glass` like every
//     other button in the tool, so they belong to WD's chrome rather than importing GLD's.
//   • The cluster TRACKS THE PANE. It sits just off the pane's right edge and slides with
//     it on collapse/expand — the old footer button froze in place and faded, which is
//     what Ryan flagged. CSS owns the motion (see `.ds-actions-cluster`).
//
//   const actions = createCanvasActions({ onSave, onShare });
//   canvasEl.appendChild(actions.el);
// ============================================================================

import { h } from '../dom.js';
import { createToolCluster } from './tool-cluster.js';
import { ICON_PDF } from '../pdf-sheets.js';
import { ICON_PROJECTS } from '../project-push.js';

const ICON_SAVE =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>';
const ICON_SHARE =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>' +
  '<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>';

/** One action row inside a card: icon + label + sub, full width. */
/** Group an item flagged `pair` with the one after it; everything else stays on its own line. */
function pairUp(items) {
  const out = [];
  for (let i = 0; i < items.length; i += 1) {
    if (items[i] && items[i].pair && items[i + 1]) { out.push([items[i], items[i + 1]]); i += 1; }
    else out.push([items[i]]);
  }
  return out;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ ONE RENDERER, BECAUSE THERE ARE TWO
   CALLERS. The card builder paired its rows and `setItems` — which rebuilds a card's rows every
   time it OPENS — did not: it appended flat `cardRow`s and swept only `.wd-ca-row`, leaving the
   emptied `.wd-ca-pair` wrapper behind. So Download and "Show me…" were side by side exactly once,
   during mount, and stacked again the first time anybody opened the card. Which is to say: nobody
   ever saw the two-up. Ryan: *"the Download and Show me... buttons are still stacked."*
   ⚠️ A REBUILD THAT DROPS A LAYOUT THE BUILDER APPLIES IS THE BUG SHAPE, not the pairing itself —
   any future row flag has to go through here for the same reason. */
function rowNodes(items) {
  return pairUp(items || []).map((row) => (row.length === 2
    ? h('div', { class: 'wd-ca-pair' },
      cardRow(row[0].icon, row[0].label, row[0].sub, row[0].onClick),
      cardRow(row[1].icon, row[1].label, row[1].sub, row[1].onClick))
    : cardRow(row[0].icon, row[0].label, row[0].sub, row[0].onClick)));
}

function cardRow(icon, label, sub, onClick) {
  const btn = h('button', { type: 'button', class: 'wd-ca-row wd-flat-glass wd-flat-glass--flush', onClick: (e) => onClick(e, btn) },
    h('span', { class: 'wd-ca-row__icon', 'aria-hidden': 'true', html: icon }),
    h('span', { class: 'wd-ca-row__text' },
      h('span', { class: 'wd-ca-row__label' }, label),
      sub ? h('span', { class: 'wd-ca-row__sub' }, sub) : null),
  );
  return btn;
}

/**
 * @param {object} cfg
 * @param {Array<{icon:string,label:string,sub?:string,onClick:Function}>} cfg.saveItems
 * @param {Array<{icon:string,label:string,sub?:string,onClick:Function}>} cfg.shareItems
 * @param {Array<{icon:string,label:string,sub?:string,onClick:Function}>} [cfg.pdfItems] rep-only;
 *   an empty/absent list means the third button is never built
 * @param {Array<{icon:string,label:string,sub?:string,onClick:Function}>} [cfg.projectItems] rep-only;
 *   same contract as pdfItems — empty/absent and the fourth button is never built
 * @param {Function} [cfg.onProjectsOpen] fired before the Projects card is shown. Its one job is to
 *   call `setItems('projects', …)` with a fresh list so the staged-plate count is live (2026-07-31).
 * @param {Function} [cfg.onSaveOpen] the same contract for the SAVE card (2026-08-07). Its rows are
 *   built once at boot, so anything in them that COUNTS something — "Download all 3 on one image" —
 *   is frozen at the count that existed then. A buyer who returns to Pick Weights and adds a model
 *   is exactly the person that row is for, and they were the one seeing a stale number.
 * @param {HTMLElement} [cfg.designsPanel] rep-only panel rendered INSIDE the Save card, above its
 *   rows. Absent for a buyer, whose Save card is then unchanged.
 */
export function createCanvasActions(cfg = {}) {
  const cluster = createToolCluster({ ariaLabel: 'Design actions' });
  cluster.el.classList.add('ds-actions-cluster');

  const cards = [];
  // (A `soon: true` variant built a dimmed, inert twin of cardRow for not-yet-wired actions. Its
  // ONE consumer was "Download PDF", removed 2026-07-25, so the branch went with it rather than
  // sitting here unreachable waiting to be revived by accident. Every row in a card is a real
  // action again.)
  /**
   * A card: a title, an optional caller-owned PANEL, then the action rows.
   *
   * The panel exists for content richer than a row list — the rep's save menu (name field, split
   * button, search, the list of designs). It goes ABOVE the rows because when a rep opens Save the
   * first thing they want is to name and keep the design; "Download image" is the secondary,
   * one-click action and reads correctly as a footer to it, under a hairline.
   *   Ryan 2026-07-29: *"don't we want to combine both in the save (floppy disc) menu/button?"* —
   * and he was right for a reason worth writing down: the floppy card used to hold ONLY "Download
   * image", so the one button labelled Save was the one that couldn't save anything.
   */
  const makeCard = (id, title, items, panel) => {
    const card = h('div', { class: `wd-ca-card wd-flat-glass${panel ? ' wd-ca-card--wide' : ''}`, id: `wd-ca-${id}` },
      h('div', { class: 'wd-ca-card__title' }, title),
      panel || null,
      panel && items.length ? h('div', { class: 'wd-ca-card__rule' }) : null,
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — TWO ROWS CAN SHARE A LINE. An item may
         ask to pair with the next one (`pair: true`), and the two render side by side in a single
         flex row instead of stacking. Ryan, on the save card's height: *"I think we can preserve
         more space by positioning the 'Download image' button and the 'Show me what's possible...'
         button side-by-side instead."*
         ⚠️ THE ITEM ASKS, THE SHELL DOES NOT DECIDE. Which two belong together is a content
         question — the same cluster renders the share list and the PDF list, and pairing by
         position would put whatever happened to be adjacent into a two-up. */
      ...rowNodes(items),
    );
    cluster.mount(card);
    return card;
  };

  /**
   * @param {Function} [onOpen] fired whenever this card becomes visible — a hover-peek, a click-pin or
   *   a swap from its sibling. Added for "Share via…" (Ryan 2026-07-30): `navigator.share()` needs
   *   transient user activation, so the share LINK has to exist before the row is tapped, not be
   *   fetched after it. Must therefore be cheap and idempotent — a mouse crossing the cluster fires
   *   this several times a second (`warmShareLink` dedupes on the design's own autosave envelope).
   */
  const mk = (id, title, icon, items, panel, onOpen) => {
    const btn = cluster.addPopoverButton({ id, title, icon });
    const card = makeCard(id, title, items || [], panel);
    btn.setAttribute('aria-controls', card.id);
    cards.push({ btn, card, onOpen });
    btn.addEventListener('click', (e) => { e.stopPropagation(); toggle(card); });
    btn.addEventListener('mouseenter', () => peek(card));
    btn.addEventListener('mouseleave', schedulePeekOut);
    card.addEventListener('mouseenter', clearHide);
    card.addEventListener('mouseleave', schedulePeekOut);
    return btn;
  };

  // HOVER peeks, CLICK pins — the zoom popover's model exactly (Ryan 2026-07-25). A card drops on
  // mouseenter and retracts shortly after you leave; clicking makes it stay and drops the GLD pin dot
  // on the button, and it then survives until another click or Escape.
  //   `pinned` is tracked per card because two buttons share the cluster: hovering the other one must
  // not silently discard a pinned card, and un-pinning must not fire from a stray mouseleave.
  let pinnedCard = null;
  let hideTimer = null;
  const clearHide = () => clearTimeout(hideTimer);
  /**
   * @param {boolean} on
   * @param {boolean} [instant] collapse with NO transition — used only when this card is being
   *   SWAPPED for its sibling. Both cards are absolutely positioned at the same spot, so an animated
   *   close overlapping an animated open stacks two sets of rows and reads as jumbled text for the
   *   length of the transition (Ryan 2026-07-25). Dropping the outgoing one instantly leaves exactly
   *   one card on screen at any moment. A plain mouse-out still animates — nothing is racing it then.
   */
  /* ⛔ THE CAP IS SET INLINE, NOT THROUGH A CUSTOM PROPERTY. The first version handed the
     stylesheet a `--wd-ca-avail` and let `min(82vh, var(...))` do the arithmetic; Ryan's card still
     ran off the bottom of the window. An inline `max-height` is the one form nothing in the cascade
     can outrank, and it removes a whole class of "is the rule even winning?" from the question.

     ⚠️ AND IT RE-MEASURES ON RESIZE. A card sized on open is a card sized for the window as it was
     then. Docking a laptop, opening devtools, rotating a tablet — each leaves the old cap in place
     and the bottom hanging past the fold.

     The gap is 24px, matching the breathing room above the card rather than hugging the edge.
     Ryan: *"we should be able to see the bottom of it... like a gap between the bottom of the
     entire save menu slide-down card and the edge of the window, similar to the gap at the top."* */
  const VIEWPORT_GAP = 24;
  function sizeToViewport(card) {
    try {
      const top = card.getBoundingClientRect().top;
      const avail = Math.max(200, window.innerHeight - top - VIEWPORT_GAP);
      card.style.maxHeight = avail + 'px';
    } catch { /* a card that cannot be measured keeps the stylesheet's cap */ }
  }
  window.addEventListener('resize', () => {
    cards.forEach((c) => { if (c.card.classList.contains('is-open')) sizeToViewport(c.card); });
  });

  function setOpen(card, on, instant) {
    const entry = cards.find((c) => c.card === card);
    if (!entry) return;
    // Class only — never the `hidden` attribute. `display: none` cannot transition, which is what
    // made the first version pop instead of wipe (see .wd-ca-card in the stylesheet).
    // Tell the card it is being shown, BEFORE the transition — whoever wants to prepare something for
    // a tap needs the head start, which is the entire reason this hook exists (see `mk`).
    if (on && entry.onOpen) { try { entry.onOpen(); } catch { /* never let a warm-up break the UI */ } }
    card.classList.toggle('wd-ca-card--instant', !!instant && !on);
    /* ⛔ HOW MUCH ROOM IS ACTUALLY LEFT BELOW THIS CARD (Ryan 2026-09-01). The card's caps were
       `82vh` (and `46vh` on the designs list) — both measured against the VIEWPORT, while the card
       itself hangs from the button cluster, which sits partway down the canvas. So the cap said
       "no taller than 82% of the screen" while the card STARTED at 40% of it, and the bottom ran
       off the fold: with five saved designs, Ryan's Download-image row and the button under it
       were below the viewport, underlapping the section beneath.
         Measured here because CSS cannot read an element's own offset. Set BEFORE `is-open` so the
       first frame of the transition already has the right ceiling — setting it after produces one
       frame at the old height, which reads as a flinch. */
    if (on) sizeToViewport(card);
    /* Cleared on close so the `max-height: 0` collapse still animates — an inline cap would
       otherwise win over the stylesheet's closed state and the card would never retract. */
    if (!on) card.style.maxHeight = '';
    card.classList.toggle('is-open', on);
    card.setAttribute('aria-hidden', on ? 'false' : 'true');
    entry.btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    // NOTE: `is-active` (the blue) is deliberately NOT set here. It belongs to the PIN, not to
    // visibility — the zoom button works the same way, its mouseenter only shows the popover.
    // Lighting it on a hover-peek meant the blue was still on for the 220ms retract delay after the
    // pointer left, which read as a #006BCC flash on the way out (Ryan 2026-07-25).
  }
  function setPinned(card) {
    pinnedCard = card;
    cards.forEach((c) => {
      const on = c.card === card;
      c.btn.classList.toggle('is-pinned', on); // the dot — same class the zoom button uses
      c.btn.classList.toggle('is-active', on);  // the blue — pinned only, never on a peek
      setOpen(c.card, on, true);
    });
  }
  /** Peek on hover — never touches the pin, so leaving restores whatever was pinned. */
  function peek(card) {
    if (pinnedCard) return; // a pinned card owns the cluster; don't flicker another over it
    clearHide();
    cards.forEach((c) => setOpen(c.card, c.card === card, true));
  }
  function schedulePeekOut() {
    clearHide();
    hideTimer = setTimeout(() => { if (!pinnedCard) cards.forEach((c) => setOpen(c.card, false)); }, 220);
  }
  /** Click = pin / unpin. Written as an explicit pair, not a toggle: the hover-show that fires on the
   *  way to the click would otherwise make the first click read as "already open" and close it. */
  function toggle(card) {
    clearHide();
    if (pinnedCard === card) { setPinned(null); cards.forEach((c) => setOpen(c.card, false)); }
    else setPinned(card);
  }
  /** `blur` matters (Ryan 2026-07-25): clearing is-active/is-pinned drops the blue and the dot, but the
   *  button KEEPS DOM focus, and pressing Escape flips the browser into keyboard mode so
   *  `.ds-tool-btn:focus-visible` then paints a ring — the button still reads as pressed. Blurring
   *  returns it to exactly its pre-click appearance. */
  function closeAll() {
    const wasPinned = pinnedCard;
    setPinned(null);
    cards.forEach((c) => setOpen(c.card, false));
    if (wasPinned) {
      const entry = cards.find((c) => c.card === wasPinned);
      if (entry && document.activeElement === entry.btn) entry.btn.blur();
    }
  }

  // ONE Save button, holding both halves: the rep's designs panel (when there is one) and the
  // download rows. A buyer passes no panel and gets exactly the card they always had.
  mk('save', 'Save', ICON_SAVE, cfg.saveItems || [], cfg.designsPanel || null, cfg.onSaveOpen || null);
  mk('share', 'Share', ICON_SHARE, cfg.shareItems || [], null, cfg.onShareOpen || null);
  // PDFs sit to the RIGHT of Share (Ryan 2026-07-29, matching the GLD's arrangement) and
  // only exist for a rep: `pdfItems()` returns [] off the portal, so a buyer's cluster is
  // the same two buttons it has always been. Built through the same `mk` as the others,
  // which is the point — it inherits hover-peek, click-to-pin, the swap behaviour and
  // Escape without any of it being written twice.
  if ((cfg.pdfItems || []).length) mk('pdf', 'PDFs', ICON_PDF, cfg.pdfItems);
  // PROJECTS sits to the right of PDFs, and for the same reasons in the same order: a rep's
  // actions on a design run save → share → document → send it somewhere (Ryan 2026-07-30 —
  // the Weight Designer feeding the Projects hub's proposal packets). Rep-only via
  // `projectItems()`, built through the same `mk` as the other three, which is again the
  // point — hover-peek, click-to-pin, the swap and Escape all come for free rather than
  // being written a fourth time.
  if ((cfg.projectItems || []).length) mk('projects', 'Projects', ICON_PROJECTS, cfg.projectItems, null, cfg.onProjectsOpen || null);

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-07-31) — REBUILD ONE CARD'S ROWS.
     The Projects card now reads "Review & send (3)…", and that 3 has to be LIVE: a rep primes a
     plate, the card is not open, and the next time it drops the number must be right. It can't
     update itself — `makeCard` builds its rows once and `cardRow` takes plain values, not
     getters — so the count would be frozen at whatever it was when the shell booted.
       Driven from the `onOpen` hook `mk` already takes (added for "Share via…"), which fires
     before the card is shown, so the rebuild happens off screen. Deliberately dumb: drop the
     rows, append the new ones. The title, the caller-owned panel and the hairline are untouched
     because they aren't `.wd-ca-row`, so the ordering (title → panel → rule → rows) survives
     without being restated here. No other card passes onOpen anything that calls this, so no
     other card's behaviour changes. */
  function setItems(id, items) {
    const entry = cards.find((c) => c.card.id === `wd-ca-${id}`);
    if (!entry) return;
    /* ⛔ THE PAIR WRAPPERS GO TOO. Sweeping only `.wd-ca-row` left an empty `.wd-ca-pair` div on
       the card, which then sat above the fresh rows as a phantom gap. */
    entry.card.querySelectorAll('.wd-ca-row, .wd-ca-pair').forEach((n) => n.remove());
    for (const node of rowNodes(items)) entry.card.appendChild(node);
  }

  // Click-away / Escape, matching the zoom popover's dismissal.
  const onDocDown = (e) => { if (!cluster.el.contains(e.target)) closeAll(); };
  const onKey = (e) => { if (e.key === 'Escape') closeAll(); };
  document.addEventListener('pointerdown', onDocDown, true);
  document.addEventListener('keydown', onKey);

  return {
    el: cluster.el,
    closeAll,
    setItems,
    destroy() {
      document.removeEventListener('pointerdown', onDocDown, true);
      document.removeEventListener('keydown', onKey);
    },
  };
}
